import { useEffect, useMemo, useState } from "react";
import TagManager from "react-gtm-module";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Seperator } from "components/Seperator";
import { MainBanner } from "components/banners/MainBanner";
import { OriginalSection } from "components/sections/OriginalSection";
import { RecentSection } from "components/sections/RecentSection";
import { RecommendSection } from "components/sections/RecommendSection";
import {
  MixContentDocument,
  Mix_Content_Status_Enum,
  Order_By,
  SessionDocument,
} from "graphql/generated";
import { getClient } from "utils/client";
import { ORDERED_MIX_CONTENT_TAG_ENUM } from "utils/enum";

const LIMIT = {
  recommend: 2,
  original: 8,
  recent: 60,
};

const buildQueryVariables = (
  section: IndexPageContentSection | null,
  tag: Mix_Content_Tag_Enum_Enum | null,
  limit: number,
): MixContentQueryVariables => ({
  where: {
    status: {
      _eq: Mix_Content_Status_Enum.Published,
    },
    ...(tag
      ? {
          mix_content_tags: {
            mix_content_tag_enum: {
              value: {
                _eq: tag,
              },
            },
          },
        }
      : {}),
    ...(section === "recommend"
      ? {
          isrecommend: {
            _eq: true,
          },
        }
      : section === "original"
      ? {
          isoriginal: {
            _eq: true,
          },
        }
      : section === "recent"
      ? {
          isoriginal: {
            _eq: false,
          },
        }
      : {}),
  },
  orderBy:
    section === "recommend"
      ? [{ recommendorder: Order_By.Asc }]
      : [{ updatedate: Order_By.Desc }, { id: Order_By.Asc }],
  limit,
  offset: 0,
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionSeperator = styled(Seperator)`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    width: auto;
    margin: 32px calc(-1 * var(--page-padding));
  }
`;

const Box = styled.div`
  &:first-of-type {
    @media (max-width: 768px) {
      margin-top: calc(-1 * var(--header-margin) + 8px);

      ${SectionSeperator} {
        display: none;
      }
    }
  }

  &:not(:first-of-type) {
    margin-top: 80px;

    @media (max-width: 768px) {
      margin-top: 0;
    }
  }
`;

const Page = () => {
  const router = useRouter();

  const tag = useMemo(
    () =>
      ORDERED_MIX_CONTENT_TAG_ENUM.find((tag) => tag === router.query.tag) ??
      null,
    [router],
  );

  const [touched, setTouched] = useState<
    Record<IndexPageContentSection, boolean>
  >({
    recommend: false,
    recent: false,
    original: false,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const sections = ["recommend", "recent", "original"];
          const targetId = entry.target.getAttribute("data-observer-id");

          if (typeof targetId === "string" && sections.includes(targetId)) {
            const section = targetId as IndexPageContentSection;

            if (entry.isIntersecting && !touched[section]) {
              TagManager.dataLayer({
                dataLayer: {
                  event: "scroll_depth",
                  scrolled_section: section,
                },
              });

              setTouched((prev) => ({ ...prev, [section]: true }));
            }
          } else {
            throw new Error("targetId is not a section!");
          }
        }
      },
      {
        root: document.querySelector("index-page-container"),
        rootMargin: "0% 0px -100% 0px",
      },
    );

    const elements = document.querySelectorAll("[data-observer-id]");
    elements.forEach((el) => observer.observe(el));

    return () => elements.forEach((el) => observer.unobserve(el));
  }, [touched]);

  return (
    <>
      <Head>
        <link rel="canonical" href="https://mix.day/" />
      </Head>
      <Container id="index-page-container">
        {tag === null && (
          <>
            <Box>
              <SectionSeperator />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  row-gap: 72px;

                  @media (max-width: 1024px) {
                    row-gap: 56px;
                  }

                  @media (max-width: 768px) {
                    row-gap: 28px;
                  }
                `}
              >
                <MainBanner
                  css={css`
                    display: none;

                    @media (max-width: 768px) {
                      display: block;
                    }
                  `}
                />
                <RecommendSection
                  data-observer-id="recommend"
                  contentSection="recommend"
                  variables={buildQueryVariables(
                    "recommend",
                    null,
                    LIMIT.recommend,
                  )}
                />
                <MainBanner
                  css={css`
                    display: block;

                    @media (max-width: 768px) {
                      display: none;
                    }
                  `}
                />
              </div>
            </Box>
            <Box>
              <SectionSeperator />
              <OriginalSection
                data-observer-id="original"
                contentSection="original-main"
                variables={buildQueryVariables(
                  "original",
                  null,
                  LIMIT.original,
                )}
              />
            </Box>
          </>
        )}
        <Box>
          <SectionSeperator />
          <RecentSection
            data-observer-id="recent"
            contentSection="recent"
            variables={buildQueryVariables("recent", tag, LIMIT.recent)}
          />
        </Box>
      </Container>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = getClient({
    headers: {
      cookie: ctx.req.headers.cookie,
    },
    ctx,
  });

  const tag =
    ORDERED_MIX_CONTENT_TAG_ENUM.find((tag) => tag === ctx.query?.tag) ?? null;

  try {
    await client.query<MixContentQuery, MixContentQueryVariables>({
      query: MixContentDocument,
      variables: buildQueryVariables("recommend", null, LIMIT.recommend),
    });

    await client.query<MixContentQuery, MixContentQueryVariables>({
      query: MixContentDocument,
      variables: buildQueryVariables("original", null, LIMIT.original),
    });

    await client.query<MixContentQuery, MixContentQueryVariables>({
      query: MixContentDocument,
      variables: buildQueryVariables("recent", tag, LIMIT.recent),
    });

    await client.query<SessionQuery, SessionQueryVariables>({
      query: SessionDocument,
    });

    return {
      props: {
        __APOLLO_CACHE_DATA__: client.extract(),
      },
    };
  } catch (error) {
    return {
      props: {
        __APOLLO_CACHE_DATA__: client.extract(),
      },
    };
  }
};

export default Page;
